import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import type { FieldValues, UseFormProps, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodErrorMap, ZodType, ZodTypeDef } from "zod";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/**
 * Zod error map that localizes the common issue codes through i18next.
 *
 * Conventions it establishes for every schema in the app:
 * - `z.string().min(1)` (or a missing/null value) reads as "required".
 * - `z.string().min(n)` for n > 1 reads as the min-length message with count.
 * - `z.string().email()` reads as the invalid-email message.
 * - `.refine(..., { params: { i18nKey: "validation.passwordMismatch" } })`
 *   renders `t(i18nKey, params)` — use this for any custom rule instead of
 *   hardcoding English in the schema.
 * - Anything else falls back to zod's default (developer-facing) message;
 *   add an `i18nKey` param when such an issue can actually reach users.
 */
export function createLocalizedErrorMap(t: TFunction): ZodErrorMap {
  return (issue, ctx) => {
    switch (issue.code) {
      case "invalid_type":
        if (issue.received === "undefined" || issue.received === "null") {
          return { message: t("validation.required") };
        }
        break;
      case "too_small":
        if (issue.type === "string") {
          const min = Number(issue.minimum);
          return min <= 1
            ? { message: t("validation.required") }
            : { message: t("validation.minLength", { count: min }) };
        }
        break;
      case "invalid_string":
        if (issue.validation === "email") {
          return { message: t("validation.invalidEmail") };
        }
        break;
      case "custom": {
        const params = issue.params as Record<string, unknown> | undefined;
        const key = params?.i18nKey;
        if (typeof key === "string") {
          return { message: t(key, { defaultValue: ctx.defaultError, ...params }) };
        }
        break;
      }
      default:
        break;
    }
    return { message: ctx.defaultError };
  };
}

export type UseZodFormOptions<TInput extends FieldValues, TOutput> = Omit<
  UseFormProps<TInput, unknown, TOutput>,
  "resolver"
>;

/**
 * The single way to create a form in this app: react-hook-form wired to a zod
 * schema, validating `onTouched`, with zod issues localized via the shared
 * error map. Field values are typed from the schema's input, and the values
 * handed to submit handlers are typed from its output (so `.transform()`s
 * flow through).
 */
export function useZodForm<TInput extends FieldValues, TOutput>(
  schema: ZodType<TOutput, ZodTypeDef, TInput>,
  options?: UseZodFormOptions<TInput, TOutput>,
): UseFormReturn<TInput, unknown, TOutput> {
  const { t } = useTranslation();

  // The resolver closes over a ref so validation always uses the active
  // language without recreating the resolver on every language change.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  const resolver = useMemo(
    () =>
      zodResolver<TInput, unknown, TOutput>(schema, {
        path: [],
        async: true,
        errorMap: (issue, ctx) => createLocalizedErrorMap(tRef.current)(issue, ctx),
      }),
    [schema],
  );

  return useForm<TInput, unknown, TOutput>({
    mode: "onTouched",
    ...options,
    resolver,
  });
}
